import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, statsApi } from '../api/api';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin]     = useState(null);   // { username, role }
  const [loading, setLoading] = useState(true);   // true while we verify stored token

  // On mount — restore session only if JWT is still valid on the server
  useEffect(() => {
    let cancelled = false;

    async function restore() {
      const token    = localStorage.getItem('admin_token');
      const username = localStorage.getItem('admin_username');
      if (!token || !username) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        const res = await statsApi.get();
        if (cancelled) return;
        if (res && res.success) {
          setAdmin({ username, role: 'admin' });
        } else {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_username');
          setAdmin(null);
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_username');
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restore();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (username, password) => {
    const res = await authApi.login(username, password);
    if (!res.success) throw new Error(res.message || 'Login failed');

    localStorage.setItem('admin_token',    res.data.token);
    localStorage.setItem('admin_username', res.data.username);
    setAdmin({ username: res.data.username, role: res.data.role });
    return res;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setAdmin(null);
  }, []);

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
}
